import { Suspense, useEffect, useState } from 'react'
import { CatchBoundary } from '@tanstack/react-router'
import { AnimateNumber } from '@/components/custom/animate-number'

/**
 * The profiles counter, PURE: two landing routes render it, so it takes the count as a plain prop
 * (AGENTS.md — a shared component receives data, never query options).
 *
 * The boundary stays HERE rather than in the routes: the counter must never block or break the page, so
 * the route hands it a `<Suspense>`-wrapped reader (`children`) and this renders 0 while that resolves,
 * or forever if it fails.
 */
export function ProfilesCount(props: { className?: string; children: React.ReactNode }) {
  return (
    <CatchBoundary
      getResetKey={() => 'profiles-count'}
      errorComponent={() => <AnimateNumber value={0} className={props.className} />}
    >
      <Suspense fallback={<AnimateNumber value={0} className={props.className} />}>{props.children}</Suspense>
    </CatchBoundary>
  )
}

/** The counter itself, once the route has read the count. */
export function ProfilesCountValue(props: { className?: string; profilesCount: number }) {
  // Mount at 0, then bump to the real count so the counter animates UP from zero. Starting at 0 also
  // keeps the server and first client render identical (both "0") — the real count arrives via an
  // effect, after hydration, so it can never mismatch.
  const [value, setValue] = useState(0)
  // react-doctor-disable-next-line react-hooks-js/set-state-in-effect -- deliberate: mount at 0 then bump post-hydration to animate up + keep SSR/first-client render identical (see above)
  // oxlint-disable-next-line react/set-state-in-effect -- deliberate: mount at 0 then bump post-hydration so the counter animates UP, and so SSR and the first client render are identical
  useEffect(() => setValue(props.profilesCount), [props.profilesCount])

  return <AnimateNumber value={value} className={props.className} />
}
